/**
 * TesoreriaDelDia.jsx — los cuatro pasos de la tesorería, en el vocabulario del
 * Yool DS (el que usa `YoolPayCierre`).
 *
 * EL ORDEN ES EL REQUISITO, NO UN DETALLE DE LAYOUT. Cada paso se apoya en el
 * anterior:
 *
 *   1. Global del día      — cuánto se cobró, cuánto cuesta el PSP, cuánto se gira.
 *   2. Por compañía        — NUEVO y COBRO. En COBRO va la validación de lo
 *                            efectivamente cobrado; en NUEVO no, porque esa
 *                            dimensión no existe ahí: o se vendió o no existe.
 *   3. Distribución        — el reparto del bruto → lo que se gira a cada una.
 *                            Es la parte que no falla.
 *   4. Traspaso a Yool     — (a) la diferencia por comisión del día y (b) los otros
 *                            cargos del PSP.
 *
 * LO QUE ESTA PANTALLA NO PUEDE HACER
 * -----------------------------------
 * Decir `$0` cuando no sabe. El saldo del PSP viene en `null` y va a seguir así
 * hasta que el payout tenga su certificado mTLS; los otros cargos no tienen fuente
 * de datos todavía. Los dos se muestran como texto («saldo no disponible», «sin
 * configurar»), nunca como una cifra: un cero ahí haría girar sin reponer.
 *
 * Y no calcula nada: todo sale de `lib/yoolpay/tesoreriaVista.js`, que es el mismo
 * módulo que consume `YoolPayDia`. Si las dos pantallas mostraran cifras distintas
 * de la misma plata, la culpa sería de haber calculado aquí.
 *
 * @see src/lib/yoolpay/tesoreriaVista.js — de dónde salen todas las cifras
 * @see src/components/payments/yoolpay/cierre/piezas.jsx — el template del DS
 */
import React, { useMemo } from 'react';

import {
  Aviso,
  Cifra,
  Encabezado,
  Fila,
  Pie,
  Rotulo,
  Seccion,
} from './cierre/piezas';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp, fmtFechaIso, fmtNum } from '../../../lib/yoolpay/formato';
import {
  decisionDelDia,
  filasPorCompania,
  hayTesoreria,
  pasoGlobal,
  traspasoAYool,
  VEREDICTO,
  accionesDelDia,
} from '../../../lib/yoolpay/tesoreriaVista';

/** Las mismas medidas para el encabezado y las filas del cuadro por compañía. */
const COLS = 'minmax(180px, 1fr) 68px 130px 120px 130px';

/** Una cifra con su rótulo, para las tarjetas del global. */
function Metrica({ label, valor, color, hint }) {
  return (
    <div style={{ minWidth: 130 }}>
      <Rotulo>{label}</Rotulo>
      <div style={{ marginTop: 4 }}>
        <Cifra size={20} weight={700} color={color} align="left">{valor}</Cifra>
      </div>
      {hint ? (
        <p style={{ margin: '3px 0 0', fontSize: 11.5, color: 'var(--yool-muted)' }}>{hint}</p>
      ) : null}
    </div>
  );
}

/** `73%` · `—` si todavía no se resolvió ninguna cuota. */
const fmtTasa = (t) => (t == null ? '—' : `${Math.round(t * 100)}%`);

/** Un monto en UF, que es como está pactada la cuota. Nunca se convierte a CLP. */
const fmtUf = (v) => (v == null ? '—' : `${new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 2, maximumFractionDigits: 4,
}).format(v)} UF`);

export default function TesoreriaDelDia({ data }) {

  const tesoreria = data?.tesoreria ?? null;

  const global = useMemo(() => pasoGlobal(tesoreria), [tesoreria]);
  const filas = useMemo(() => filasPorCompania(tesoreria), [tesoreria]);
  const traspaso = useMemo(() => traspasoAYool(tesoreria, null), [tesoreria]);
  const decision = useMemo(() => decisionDelDia(data), [data]);

  // Sin tarifa del PSP no hay tesorería que mostrar, y se dice por qué: una sección
  // vacía se leería como «hoy no hubo movimiento».
  if (!hayTesoreria(data) || !global) {
    return (
      <Seccion titulo="Tesorería del día">
        <div style={{ padding: 20 }}>
          <Aviso tono="alta" titulo="No se pudo calcular.">
            Falta la tarifa del PSP en <code>fee_definitions</code>. Sin ella, la
            diferencia que hay que reponer sería un número inventado, así que no se
            muestra ninguna.
          </Aviso>
        </div>
      </Seccion>
    );
  }

  const hayCobranza = filas.some((f) => f.tiene_cobranza);

  return (
    <div style={{ display: 'grid', gap: 16 }}>

      {/* ─── QUÉ HACER HOY ──────────────────────────────────────────────────
          Va ARRIBA DE TODO y es lo único que instruye: los cuatro pasos de abajo
          explican de dónde salen los números, pero el manager entra a esta
          pantalla a saber qué ejecutar. Enterrar la instrucción bajo el análisis
          obliga a leer la jornada entera para llegar a ella. */}
      {decision ? (
        <Seccion
          titulo="Qué hacer hoy"
          bajada={
            'Lo único de esta pantalla que instruye. El resto explica de dónde salen '
            + 'los números.'
          }
        >
          <div style={{ padding: '18px 24px', display: 'grid', gap: 16 }}>
            <div
              style={{
                display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start',
              }}
            >
              <Metrica
                label="Traspasar a la cuenta"
                valor={fmtClp(decision.traspaso?.monto_clp)}
                color={decision.traspaso?.monto_clp ? CIFRA.rojo : CIFRA.apagado}
                hint={decision.traspaso?.antes_de
                  ? `Antes del ${fmtFechaIso(decision.traspaso.antes_de)}`
                  : undefined}
              />
              <p
                style={{
                  margin: 0, fontSize: 12.5, lineHeight: 1.55,
                  color: 'var(--yool-gray1)', maxWidth: 560, paddingTop: 14,
                }}
              >
                {decision.traspaso?.motivo}
              </p>
            </div>

            {decision.por_compania.length === 0 ? (
              <Aviso tono="baja" titulo="No hay giros que decidir.">
                Este día no dejó plata que girar a ninguna compañía.
              </Aviso>
            ) : (
              /*
                UN SOLO AVISO PARA EL DEPÓSITO. El PSP deposita un monto por el lote, no
                uno por aseguradora: partirlo mandaba a buscar en la cartola movimientos
                que no existen. Los giros SÍ siguen por compañía — entra una transferencia
                y salen N.
              */
              <div style={{ display: 'grid', gap: 9 }}>
                {accionesDelDia(decision).map((a) => (
                  <Aviso
                    key={a.id}
                    tono={a.tono}
                    titulo={`${a.titulo} · ${fmtClp(a.monto_clp)}`}
                  >
                    {a.motivo}
                    {a.dias_vencido > 0 ? (
                      <strong> Lleva {fmtNum(a.dias_vencido)} día
                        {a.dias_vencido === 1 ? '' : 's'} de atraso.
                      </strong>
                    ) : null}
                    {a.companias.length > 0 ? (
                      <span style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        Cubre el giro de {a.companias.length} compañía
                        {a.companias.length === 1 ? '' : 's'}:{' '}
                        {a.companias
                          .map((c) => `${c.nombre} ${fmtClp(c.monto_clp)}`)
                          .join(' · ')}
                      </span>
                    ) : null}
                  </Aviso>
                ))}
              </div>
            )}
          </div>
        </Seccion>
      ) : null}

      {/* ─── PASO 1 · El global del día ─────────────────────────────────────── */}
      <Seccion
        titulo="1 · El día, en total"
        bajada={
          'Cuánto se recaudó, cuánto retiene el PSP y cuánto hay que girar. '
          + `El giro sale el ${fmtFechaIso(global.fecha_giro)}.`
        }
      >
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', padding: '18px 24px' }}>
          <Metrica
            label="Cobros"
            valor={fmtNum(global.cobros)}
            hint={`${fmtNum(global.venta_nueva?.cobros ?? 0)} nuevos · ${fmtNum(global.cobranza?.cobros ?? 0)} de cobro`}
          />
          <Metrica
            label="Debo recaudar"
            valor={fmtClp(global.debo_recaudar_clp)}
            color={CIFRA.tinta}
            hint="El bruto cobrado"
          />
          <Metrica
            label="Retiene el PSP"
            valor={fmtClp(global.diferencia_psp_clp)}
            color={CIFRA.ambar}
            hint={global.diferencia_psp_clp == null
              ? 'El PSP no informó su comisión en algún cobro'
              : 'Lo que informó el proveedor · costo de Yool, no de la compañía'}
          />
          {/*
            EL NETO QUE EL PSP DICE QUE VA A DEPOSITAR. Es la cifra contra la que se
            compara la cartola, y no estaba en ninguna parte: se mostraba el bruto, la
            comisión y lo que hay que girar, pero no cuánto entra al banco. Sin ella
            «depositado por el PSP: —» no tiene referencia y un depósito corto recién se
            ve al cuadrar el mes.
          */}
          <Metrica
            label="Esperado por depositar"
            valor={fmtClp(global.esperado_depositar_clp)}
            color={CIFRA.tinta}
            hint={global.esperado_depositar_clp == null
              ? 'No se puede saber: falta el dato del PSP en algún cobro'
              : 'Lo que el PSP declara depositar · contra esto se revisa la cartola'}
          />
          <Metrica
            label="A girar"
            valor={fmtClp(global.a_girar_clp)}
            color={CIFRA.verde}
            hint="El 100% del bruto"
          />
        </div>
      </Seccion>

      {/* ─── PASO 2 · Por compañía: nuevo y cobro ───────────────────────────── */}
      <Seccion
        titulo="2 · Por compañía: venta nueva y cobro"
        bajada={
          'La venta nueva es lo que se vendió hoy; el cobro es cartera, y ahí sí hay '
          + 'que validar lo efectivamente cobrado. Abre una compañía para ver las '
          + 'transacciones que respaldan cada cifra.'
        }
      >
        {filas.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Aviso tono="baja" titulo="Sin movimiento.">
              Ninguna compañía cobró ni tenía cuotas de cobro este día.
            </Aviso>
          </div>
        ) : (
          <>
            <Encabezado cols={COLS}>
              <Rotulo>Compañía</Rotulo>
              <Rotulo align="right">Cobros</Rotulo>
              <Rotulo align="right">Debo recaudar</Rotulo>
              <Rotulo align="right">Diferencia PSP</Rotulo>
              <Rotulo align="right">A girar</Rotulo>
            </Encabezado>

            {filas.map((f) => {
              // Sin plata y con cobranza esperada = la cartera falló entera. Es el
              // caso que hay que ver, así que la fila se atenúa pero NO se esconde.
              const sinPlata = f.debo_recaudar_clp === 0;
              return (
                <React.Fragment key={f.compania_id}>
                  <Fila cols={COLS} tenue={sinPlata}>
                    {/* TEXTO Y NO BOTÓN: sin desplegable, un chevron promete algo que no
                        ocurre. */}
                    <span
                      title={f.nombre}
                      style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--yool-black)',
                        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.nombre}
                    </span>
                    <Cifra color={f.cobros ? CIFRA.tinta : CIFRA.apagado}>
                      {fmtNum(f.cobros)}
                    </Cifra>
                    <Cifra>{fmtClp(f.debo_recaudar_clp)}</Cifra>
                    <Cifra color={CIFRA.ambar}>{fmtClp(f.diferencia_psp_clp)}</Cifra>
                    <Cifra color={CIFRA.verde}>{fmtClp(f.a_girar_clp)}</Cifra>
                  </Fila>

                  {/* NUEVO y COBRO, y en COBRO la validación de lo cobrado. */}
                  <Fila cols="1fr 1fr" tenue alinear="start">
                    <div>
                      <Rotulo>Venta nueva</Rotulo>
                      <div style={{ marginTop: 3, fontSize: 12.5, color: 'var(--yool-gray1)' }}>
                        {fmtNum(f.venta_nueva?.cobros ?? 0)} cobros ·{' '}
                        {fmtClp(f.venta_nueva?.debo_recaudar_clp ?? 0)}
                      </div>
                    </div>
                    <div>
                      <Rotulo>Cobro (cartera)</Rotulo>
                      <div style={{ marginTop: 3, fontSize: 12.5, color: 'var(--yool-gray1)' }}>
                        {f.tiene_cobranza ? (
                          <>
                            {fmtNum(f.validacion.cobradas)} de{' '}
                            {fmtNum(f.validacion.esperadas)} cobradas ·{' '}
                            <strong
                              style={{
                                color: f.validacion.tasa_exito === null
                                  ? CIFRA.apagado
                                  : f.validacion.tasa_exito < 1 ? CIFRA.rojo : CIFRA.verde,
                              }}
                            >
                              {fmtTasa(f.validacion.tasa_exito)}
                            </strong>
                            {f.validacion.fallidas > 0
                              ? ` · ${fmtNum(f.validacion.fallidas)} fallidas`
                              : null}
                            {f.validacion.otras > 0
                              ? ` · ${fmtNum(f.validacion.otras)} sin resolver`
                              : null}
                            {f.validacion.no_cobrado_uf != null
                              ? ` · ${fmtUf(f.validacion.no_cobrado_uf)} sin cobrar`
                              : null}
                          </>
                        ) : (
                          <span style={{ color: 'var(--yool-muted)' }}>
                            No había cuotas de cobro este día
                          </span>
                        )}
                      </div>
                    </div>
                  </Fila>

                  {/* EL DETALLE POR TRANSACCIÓN SE FUE (2026-08-25). Mostraba las mismas
                      filas que «Transacciones del día», que además trae la hora, el id del
                      PSP, los destinos de un cobro repartido, los DOS estados y filtros.

                      NO SE FUE ANTES por una razón concreta: acá vivían las marcas ✖ (el
                      dato del PSP no cuadra) y ⚠ (la tasa efectiva se aparta), y la lista
                      plana no las tenía. Quitarlo habría perdido dos alertas. Primero se
                      llevaron allá; recién entonces esto pudo salir.

                      Las columnas de esta tabla y su validación de cobranza se mantienen:
                      son lo que ningún otro bloque muestra. */}
                </React.Fragment>
              );
            })}

            <Pie total={fmtClp(global.debo_recaudar_clp)} totalLabel="Total del día">
              {fmtNum(global.cobros)} cobros en {fmtNum(filas.length)} compañía
              {filas.length === 1 ? '' : 's'}.{' '}
              {hayCobranza
                ? 'La tasa de cobro es la señal de calidad de cartera: una caída ahí anticipa churn.'
                : 'Todo el día fue venta nueva — no había cartera que cobrar.'}
            </Pie>
          </>
        )}
      </Seccion>

      {/* EL PASO 3 SE FUE. «3 · Distribución de fondos» tenía dos columnas —Compañía y
          A girar— y las dos están en el bloque «Cuadratura del día», que además dice
          cuánto se cobró, cuánto retuvo el PSP y cuánto ya salió girado. Era un
          subconjunto estricto: mismas filas, mismas cifras, una sola columna.

          La numeración de los pasos que quedan NO se corre a propósito: «4 · Traspaso»
          se llama así en el spec y en el resto de las pantallas, y renumerarlo por un
          bloque que se fue obligaría a traducir entre dos vocabularios. */}

      {/* ─── PASO 4 · Traspaso a la cuenta de Yool ──────────────────────────── */}
      <Seccion
        titulo="4 · Traspaso a la cuenta de Yool"
        bajada={
          'Lo que hay que poner de recursos propios para poder girar, y a qué ritmo '
          + 'se está consumiendo la cuenta.'
        }
      >
        <div style={{ padding: '18px 24px', display: 'grid', gap: 18 }}>

          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <Metrica
              label="(a) Comisión del día"
              valor={fmtClp(traspaso.diferencia_psp_clp)}
              color={CIFRA.ambar}
              hint="Por las transacciones de hoy"
            />
            <Metrica
              label="(b) Otros cargos del PSP"
              valor={traspaso.cargos_periodicos_clp == null
                ? 'sin configurar'
                : fmtClp(traspaso.cargos_periodicos_clp)}
              color={traspaso.cargos_periodicos_clp == null ? CIFRA.apagado : CIFRA.ambar}
              hint="Mantención y cobros no transaccionales"
            />
            {/*
              LA CUENTA DESDE LA QUE SE GIRA, no la del PSP. El giro real es manual desde el
              banco de Yool (110011 Settlement), así que ése es el saldo que responde «¿puedo
              girar?». Antes se preguntaba al PSP —cuya consulta además falla— y por eso esta
              casilla y las dos siguientes salían vacías.
            */}
            <Metrica
              label="Saldo para girar"
              valor={traspaso.saldo_disponible
                ? fmtClp(traspaso.saldo_psp_clp)
                : 'saldo no disponible'}
              color={traspaso.saldo_disponible ? CIFRA.tinta : CIFRA.apagado}
              hint={
                traspaso.fuente_saldo === 'settlement_odoo' ? 'Cuenta Settlement, según Odoo'
                  : traspaso.fuente_saldo === 'psp' ? 'Saldo en el PSP'
                    : 'No se pudo leer'
              }
            />
            <Metrica
              label="Disponible en Collection"
              valor={traspaso.saldo_collection_clp == null
                ? 'no disponible'
                : fmtClp(traspaso.saldo_collection_clp)}
              color={traspaso.saldo_collection_clp == null ? CIFRA.apagado : CIFRA.tinta}
              hint="De aquí sale el traspaso"
            />
            <Metrica
              label="Falta poner"
              valor={traspaso.falta_poner_clp == null ? '—' : fmtClp(traspaso.falta_poner_clp)}
              color={traspaso.falta_poner_clp ? CIFRA.rojo : CIFRA.apagado}
              hint={traspaso.falta_poner_clp == null
                ? 'Depende del saldo'
                : traspaso.falta_poner_clp === 0
                  ? 'El saldo alcanza'
                  : 'Para poder girar hoy'}
            />
          </div>

          {/*
            POR QUÉ FALTA PLATA, que es lo que el bloque nunca decía. El motivo lo redacta
            payment, así que la pantalla y el servidor no pueden explicar cosas distintas.
          */}
          {traspaso.motivo_traspaso ? (
            <Aviso
              tono={traspaso.falta_poner_clp ? 'media' : 'baja'}
              titulo={traspaso.falta_poner_clp
                ? `Hay que traspasar ${fmtClp(traspaso.falta_poner_clp)} desde Collection.`
                : 'No hay que traspasar nada hoy.'}
            >
              {traspaso.motivo_traspaso}
            </Aviso>
          ) : null}

        </div>
      </Seccion>
    </div>
  );
}
